<template>
	<div id="hero-top" class="credit-landing-root">
		<!-- ========================================================================= -->
		<!-- 1. HERO BANNER WITH 3D INTERACTION & PROMINENT DEMO / WHATSAPP CTAS       -->
		<!-- ========================================================================= -->
		<section ref="heroBanner" class="credit-hero-section" @mousemove="onHeroMouseMove" @mouseleave="onHeroMouseLeave">
			<!-- Ambient lighting & spotlight glow -->
			<div class="hero-spotlight-glow" :style="{
				background: `radial-gradient(circle 550px at ${spotlightX}% ${spotlightY}%, rgba(82, 46, 168, 0.16), rgba(37, 211, 102, 0.06) 45%, transparent 75%)`
			}"></div>
			<div class="ambient-orb orb-purple"></div>
			<div class="ambient-orb orb-emerald"></div>
			<div class="ambient-grid-pattern"></div>

			<v-container class="hero-container py-10 py-md-16">
				<v-row class="align-center">
					<!-- RIGHT COLUMN (in RTL): 3D Mockup Visual & Parallax Badges -->
					<v-col cols="12" lg="6" class="order-1 order-lg-1">
						<div class="hero-visual-wrapper">
							<!-- 3D Perspective Card -->
							<div class="hero-3d-card" :style="{
								transform: `perspective(1000px) rotateX(${tiltX}deg) rotateY(${tiltY}deg) scale3d(1.02, 1.02, 1.02)`
							}">
								<!-- Top Window Mockup Header -->
								<div class="mockup-header-bar">
									<div class="mockup-dots">
										<span class="m-dot dot-red"></span>
										<span class="m-dot dot-yellow"></span>
										<span class="m-dot dot-green"></span>
									</div>
									<div class="mockup-title">
										<v-icon small color="#522ea8" class="ml-1">mdi-shield-check</v-icon>
										<span>سامانه جامع استعلام و اعتبارسنجی | جت اقساط</span>
									</div>
									<div class="mockup-status-tag">
										<span class="status-pulse-dot"></span>
										<span>استعلام برخط</span>
									</div>
								</div>

								<!-- Mockup Screen Body: Inquiry Report Interface -->
								<div class="mockup-screen-body">
									<div class="inquiry-report-mockup pa-5">
										<!-- Customer Summary Header -->
										<div class="customer-row d-flex align-center justify-between mb-4">
											<div class="d-flex align-center">
												<div class="customer-avatar-box ml-3">
													<v-icon color="#522ea8">mdi-account-check</v-icon>
												</div>
												<div>
													<div class="font-weight-black text-subtitle-1 color-dark">محمد رستمی (متقاضی خرید قسطی)</div>
													<div class="text-caption grey--text">کد ملی: ۰۳۷******۱ • استعلام شاهکار: <span class="green--text font-weight-bold">تطبیق تایید شد</span></div>
												</div>
											</div>
											<v-chip small color="#e8f5e9" text-color="#2e7d32" class="font-weight-black">
												<v-icon left x-small color="#2e7d32">mdi-check-circle</v-icon>
												ریسک پایین (A+)
											</v-chip>
										</div>

										<!-- Score & Check Results Grid -->
										<v-row dense class="mb-3">
											<v-col cols="6">
												<div class="metric-card bg-soft-green pa-3 rounded-lg text-center">
													<div class="text-caption grey--text text--darken-1 mb-1">استعلام چک برگشتی</div>
													<div class="font-weight-black green--text text--darken-2 text-body-2">
														<v-icon small color="#2e7d32">mdi-check</v-icon> فاقد چک برگشتی فعال
													</div>
												</div>
											</v-col>
											<v-col cols="6">
												<div class="metric-card bg-soft-purple pa-3 rounded-lg text-center">
													<div class="text-caption grey--text text--darken-1 mb-1">سابقه پرداخت در سیستم</div>
													<div class="font-weight-black brand-purple-text text-body-2">
														<v-icon small color="#522ea8">mdi-star-check</v-icon> ۱۰ قسط منظم، بدون تاخیر
													</div>
												</div>
											</v-col>
										</v-row>

										<!-- Action Recommendation -->
										<div class="recommendation-bar pa-3 rounded-lg d-flex align-center justify-between">
											<div class="d-flex align-center">
												<v-icon color="#2e7d32" class="ml-2">mdi-thumb-up-outline</v-icon>
												<span class="text-caption font-weight-bold color-dark">پیشنهاد سیستم: تایید با شرایط استاندارد و معمول</span>
											</div>
											<v-btn x-small rounded color="#522ea8" dark depressed @click="scrollTo('test-register')">صدور قرارداد</v-btn>
										</div>
									</div>
									<div class="mockup-glass-sheen" :style="{
										background: `linear-gradient(${120 + tiltY * 2.5}deg, rgba(255, 255, 255, 0.3) 0%, transparent 60%)`
									}"></div>
								</div>
							</div>

							<!-- Floating Parallax Badges -->
							<div class="parallax-badge badge-check" :style="{
								transform: `translate3d(${badgeX1}px, ${badgeY1}px, 40px)`
							}">
								<div class="badge-icon-box bg-purple">
									<v-icon color="#522ea8" small>mdi-bank-check</v-icon>
								</div>
								<div class="badge-text-box">
									<span class="badge-primary">استعلام برخط بانکی</span>
									<span class="badge-secondary">چک برگشتی و تسهیلات معوق</span>
								</div>
							</div>

							<div class="parallax-badge badge-risk" :style="{
								transform: `translate3d(${badgeX2}px, ${badgeY2}px, 50px)`
							}">
								<div class="badge-icon-box bg-emerald">
									<v-icon color="#25D366" small>mdi-shield-account-variant</v-icon>
								</div>
								<div class="badge-text-box">
									<span class="badge-primary">ارزیابی ریسک مشتری</span>
									<span class="badge-secondary">بر اساس رفتار واقعی پرداخت</span>
								</div>
							</div>

							<div class="parallax-badge badge-speed" :style="{
								transform: `translate3d(${badgeX3}px, ${badgeY3}px, 45px)`
							}">
								<div class="badge-icon-box bg-amber">
									<v-icon color="#e65100" small>mdi-lightning-bolt</v-icon>
								</div>
								<div class="badge-text-box">
									<span class="badge-primary">تصمیم‌گیری آگاهانه</span>
									<span class="badge-secondary">پایان فروش‌های ریسکی و حدسی</span>
								</div>
							</div>
						</div>
					</v-col>

					<!-- LEFT COLUMN (in RTL): Titles, Subtitle & Clear CTA Buttons -->
					<v-col cols="12" lg="6" class="order-2 order-lg-2 pr-lg-8">
						<div class="hero-text-content">
							<!-- Announcement Pill -->
							<div class="hero-chip mb-4">
								<span class="hero-pulse-dot"></span>
								<v-icon small color="#522ea8" class="mx-1">mdi-shield-search</v-icon>
								<span>اعتبارسنجی و استعلام آنلاین مشتریان اقساطی</span>
							</div>

							<!-- Main Hero Title -->
							<h1 class="hero-title mb-4">
								اول اعبارسنجی کن، بعد تصمیم بگیر!
								<br />
								<span class="hero-title-highlight">هر مشتری، یک ریسک متفاوت دارد.</span>
							</h1>

							<!-- Hero Subtitle -->
							<p class="hero-subtitle mb-8">
								دو مشتری ممکن است هر دو متقاضی خرید اقساطی باشند، اما توانایی بازپرداخت و سابقه چک و تعهداتشان هرگز یکسان نیست. با سامانه جامع جت اقساط، تصمیم برای فروش اقساطی را بر اساس اطلاعات و داده‌های موثق بگیرید، نه فقط بر اساس حدس و گمان یا ظاهر معامله.
							</p>

							<!-- Two Primary Prominent CTA Buttons (Demo & WhatsApp) -->
							<div class="hero-actions mb-8">
								<v-btn large elevation="4" rounded class="hero-btn-primary ml-sm-4 mb-3 mb-sm-0" @click="scrollTo('test-register')">
									<v-icon left>mdi-rocket-launch-outline</v-icon>
									<span>درخواست دمو و تست رایگان</span>
								</v-btn>

								<v-btn large elevation="3" rounded class="hero-btn-whatsapp" :href="whatsappUrl" target="_blank">
									<v-icon left size="22">mdi-whatsapp</v-icon>
									<span>مشاوره رایگان در واتساپ</span>
								</v-btn>
							</div>

							<!-- Key Value Trust Badges -->
							<div class="hero-trust-row">
								<div class="trust-item">
									<v-icon small color="#2e7d32">mdi-check-decagram</v-icon>
									<span>۱۰ روز مهلت تست رایگان</span>
								</div>
								<div class="trust-item">
									<v-icon small color="#522ea8">mdi-bank-outline</v-icon>
									<span>استعلام چک‌های برگشتی صیادی</span>
								</div>
								<div class="trust-item">
									<v-icon small color="#522ea8">mdi-account-sync-outline</v-icon>
									<span>سنجش سوابق پرداخت مشتریان</span>
								</div>
							</div>
						</div>
					</v-col>
				</v-row>
			</v-container>
		</section>

		<!-- ========================================================================= -->
		<!-- 2. DEDICATED INLINE APARAT VIDEO SECTION (NON-MODAL)                      -->
		<!-- ========================================================================= -->
		<section id="inquiry-video" class="landing-section bg-white border-bottom-subtle">
			<v-container style="max-width: 1240px;">
				<div class="section-badge-header text-center mb-10">
					<div class="section-chip mb-2">
						<v-icon small color="#e65100">mdi-play-circle-outline</v-icon>
						<span>ویدیوی معرفی و نحوه استعلام</span>
					</div>
					<h2 class="section-main-heading">
						اعتبارسنجی یعنی تصمیم بهتر، نه سخت‌تر کردن فروش!
					</h2>
					<!-- <p class="section-sub-heading">
						در این ویدیوی کوتاه ببینید که چگونه تنها با چند کلیک، سابقه چک برگشتی و ریسک مشتری بررسی و مستند می‌شود.
					</p> -->
				</div>

				<v-row class="align-center">
					<!-- Video Player Column (Inline Aparat Responsive Frame) -->
					<v-col cols="12" lg="7">
						<div class="video-card-container">
							<div class="video-card-inner">
								<div class="video-frame-wrapper">
									<iframe src="https://www.aparat.com/video/video/embed/videohash/zuwld66/vt/frame" allowFullScreen="true" webkitallowfullscreen="true" mozallowfullscreen="true" title="ویدیوی نحوه استعلام و اعتبارسنجی در جت اقساط"></iframe>
								</div>
								<div class="video-footer-strip">
									<div class="d-flex align-center">
										<div class="video-play-indicator ml-2">
											<v-icon small color="#ffffff">mdi-play</v-icon>
										</div>
										<span class="video-title-text font-weight-bold">آموزش گام‌به‌گام استعلام برخط و ارزیابی مشتریان</span>
									</div>
									<a href="https://www.aparat.com/v/zuwld66" target="_blank" class="aparat-link-btn">
										<span>مشاهده در آپارات</span>
										<v-icon x-small class="mr-1">mdi-open-in-new</v-icon>
									</a>
								</div>
							</div>
						</div>
					</v-col>

					<!-- Complementary Takeaways & Direct CTAs -->
					<v-col cols="12" lg="5" class="pr-lg-8 mt-6 mt-lg-0">
						<div class="video-side-box">
							<h3 class="video-side-title mb-4">
								با اعتبارسنجی آنلاین، قبل از تحویل کالا مطمئن شوید:
							</h3>

							<div class="takeaway-card mb-4">
								<div class="takeaway-icon bg-light-purple">
									<v-icon color="#522ea8">mdi-shield-check-outline</v-icon>
								</div>
								<div class="takeaway-text">
									<h4>استعلام لحظه‌ای چک‌های برگشتی</h4>
									<p>وضعیت سوءاثر چک‌های بانکی و خوش‌حسابی مشتری را پیش از عقد قرارداد و تحویل جنس استعلام کنید.</p>
								</div>
							</div>

							<div class="takeaway-card mb-4">
								<div class="takeaway-icon bg-light-green">
									<v-icon color="#2e7d32">mdi-history</v-icon>
								</div>
								<div class="takeaway-text">
									<h4>سوابق مشتری قدیم و جدید</h4>
									<p>مشتری قدیمی هم باید شناخته شود؛ تغییرات وضعیت شغلی و مالی مشتریان را در هر بار خرید بررسی کنید.</p>
								</div>
							</div>

							<div class="takeaway-card mb-6">
								<div class="takeaway-icon bg-light-amber">
									<v-icon color="#e65100">mdi-clipboard-text-search-outline</v-icon>
								</div>
								<div class="takeaway-text">
									<h4>بایگانی متمرکز نتایج استعلام</h4>
									<p>پایان نگهداری اطلاعات در دفتر و اکسل‌های پراکنده؛ کلیه نتایج به پرونده الکترونیک مشتری الصاق می‌شود.</p>
								</div>
							</div>

							<!-- Action Buttons in Video Section -->
							<div class="d-flex flex-wrap gap-3">
								<v-btn color="#522ea8" dark rounded large elevation="2" class="px-6 font-weight-bold" @click="scrollTo('test-register')">
									<v-icon left>mdi-play-circle</v-icon>
									درخواست دمو و شروع تست
								</v-btn>

								<v-btn outlined color="#25D366" rounded large class="px-6 font-weight-bold whatsapp-outline-btn" :href="whatsappUrl" target="_blank">
									<v-icon left color="#25D366">mdi-whatsapp</v-icon>
									مشاوره در واتساپ
								</v-btn>
							</div>
						</div>
					</v-col>
				</v-row>
			</v-container>
		</section>

		<!-- ========================================================================= -->
		<!-- 3. ESSENTIAL INQUIRY PILLARS (قبل از فروش چه اطلاعاتی مهم است؟)           -->
		<!-- ========================================================================= -->
		<section id="features" class="landing-section bg-soft-lilac">
			<v-container style="max-width: 1240px;">
				<div class="text-center mb-12">
					<div class="section-chip mb-2">
						<v-icon small color="#522ea8">mdi-information-outline</v-icon>
						<span>چک‌لیست حیاتی قبل از معامله</span>
					</div>
					<h2 class="section-main-heading">قبل از فروش قسطی، چه اطلاعاتی مهم است؟</h2>
					<p class="section-sub-heading max-w-700 mx-auto">
						«هدف این نیست که مشتری را رد کنیم؛ هدف این است که آگاهانه‌تر، با شرایط متناسب و با ریسک کنترل‌شده تصمیم بگیریم.»
					</p>
				</div>

				<v-row>
					<v-col cols="12" sm="6" md="4">
						<div class="inquiry-pillar-card">
							<div class="pillar-icon-box bg-purple">
								<v-icon color="#522ea8">mdi-card-account-details-outline</v-icon>
							</div>
							<h3 class="pillar-title">مشخصات و احراز هویت</h3>
							<p class="pillar-desc">
								تطبیق هوشمند کدملی با شماره موبایل (سامانه شاهکار) و تایید هویت خریدار برای جلوگیری از هرگونه جعل و کلاهبرداری.
							</p>
						</div>
					</v-col>

					<v-col cols="12" sm="6" md="4">
						<div class="inquiry-pillar-card">
							<div class="pillar-icon-box bg-red">
								<v-icon color="#d32f2f">mdi-check-decagram-outline</v-icon>
							</div>
							<h3 class="pillar-title">وضعیت چک‌های برگشتی</h3>
							<p class="pillar-desc">
								استعلام فوری چک‌های برگشتی صیادی و سابقه سوءاثر در سیستم بانکی برای اطمینان از سلامت دسته‌چک خریدار.
							</p>
						</div>
					</v-col>

					<v-col cols="12" sm="6" md="4">
						<div class="inquiry-pillar-card">
							<div class="pillar-icon-box bg-emerald">
								<v-icon color="#2e7d32">mdi-history</v-icon>
							</div>
							<h3 class="pillar-title">سوابق مشتری در کسب‌وکار شما</h3>
							<p class="pillar-desc">
								مشاهده تاریخچه خریدهای قبلی، بازپرداخت منظم، تاخیرهای مکرر یا خوش‌حسابی بدون نیاز به گشتن در دفاتر و فاکتورها.
							</p>
						</div>
					</v-col>

					<v-col cols="12" sm="6" md="4">
						<div class="inquiry-pillar-card">
							<div class="pillar-icon-box bg-amber">
								<v-icon color="#e65100">mdi-account-clock-outline</v-icon>
							</div>
							<h3 class="pillar-title">اعتبارسنجی مشتریان قدیم و جدید</h3>
							<p class="pillar-desc">
								مشتری قدیمی هم باید شناخته شود؛ ممکن است امروز شرایط مالی متفاوتی نسبت به خرید سال گذشته داشته باشد.
							</p>
						</div>
					</v-col>

					<v-col cols="12" sm="6" md="4">
						<div class="inquiry-pillar-card">
							<div class="pillar-icon-box bg-purple">
								<v-icon color="#522ea8">mdi-map-marker-radius-outline</v-icon>
							</div>
							<h3 class="pillar-title">اطلاعات تماس و نشانی معتبر</h3>
							<p class="pillar-desc">
								ثبت دقیق نشانی محل کار، سکونت، شماره‌های پشتیبان و تلفن‌های تایید شده برای پیگیری‌های ضروری سررسید.
							</p>
						</div>
					</v-col>

					<v-col cols="12" sm="6" md="4">
						<div class="inquiry-pillar-card">
							<div class="pillar-icon-box bg-indigo">
								<v-icon color="#303f9f">mdi-database-eye-outline</v-icon>
							</div>
							<h3 class="pillar-title">متمرکزسازی اطلاعات پراکنده</h3>
							<p class="pillar-desc">
								وقتی مشتری‌ها زیاد می‌شوند، Excel و پیام‌رسان‌ها دردسرساز می‌شوند؛ جت اقساط همه چیز را یکپارچه می‌کند.
							</p>
						</div>
					</v-col>
				</v-row>
			</v-container>
		</section>

		<!-- ========================================================================= -->
		<!-- 5. END-TO-END WORKFLOW INTEGRATION (اعتبارسنجی را بخشی از فروش کن)        -->
		<!-- ========================================================================= -->
		<section class="landing-section bg-white">
			<v-container style="max-width: 1240px;">
				<div class="text-center mb-12">
					<div class="section-chip mb-2">
						<v-icon small color="#522ea8">mdi-sitemap</v-icon>
						<span>فرآیند یکپارچه در جت اقساط</span>
					</div>
					<h2 class="section-main-heading">اعتبارسنجی را بخشی از فرآیند فروش کن 🔍</h2>
					<p class="section-sub-heading max-w-700 mx-auto">
						«اول مشتری را بشناس، بعد اعتبار بده.» همه مراحل از ثبت نام تا وصول قسط در یک پنل یکپارچه:
					</p>
				</div>

				<div class="workflow-steps-grid">
					<div class="workflow-step-card">
						<div class="step-badge-num">۱</div>
						<div class="step-icon-wrap">
							<v-icon color="#522ea8">mdi-account-plus</v-icon>
						</div>
						<h4 class="step-title">اطلاعات مشتری</h4>
						<p class="step-desc">ثبت مشخصات، شماره موبایل، آدرس و بایگانی مدارک فردی و شغلی</p>
					</div>

					<div class="workflow-connector">
						<v-icon color="#522ea8">mdi-chevron-left</v-icon>
					</div>

					<div class="workflow-step-card active-step">
						<div class="step-badge-num">۲</div>
						<div class="step-icon-wrap">
							<v-icon color="#522ea8">mdi-shield-search</v-icon>
						</div>
						<h4 class="step-title">اعتبارسنجی و استعلام</h4>
						<p class="step-desc">استعلام چک برگشتی و ارزیابی سابقه برای تصمیم‌گیری آگاهانه</p>
					</div>

					<div class="workflow-connector">
						<v-icon color="#522ea8">mdi-chevron-left</v-icon>
					</div>

					<div class="workflow-step-card">
						<div class="step-badge-num">۳</div>
						<div class="step-icon-wrap">
							<v-icon color="#522ea8">mdi-file-sign</v-icon>
						</div>
						<h4 class="step-title">تنظیم قرارداد</h4>
						<p class="step-desc">ثبت نوع وام، درصد کارمزد، تضامین و چاپ قرارداد رسمی</p>
					</div>

					<div class="workflow-connector">
						<v-icon color="#522ea8">mdi-chevron-left</v-icon>
					</div>

					<div class="workflow-step-card">
						<div class="step-badge-num">۴</div>
						<div class="step-icon-wrap">
							<v-icon color="#522ea8">mdi-calendar-month-outline</v-icon>
						</div>
						<h4 class="step-title">صدور اقساط و پیامک</h4>
						<p class="step-desc">محاسبه خودکار اقساط و ارسال زمان‌بندی‌شده پیامک‌های یادآوری</p>
					</div>

					<div class="workflow-connector">
						<v-icon color="#522ea8">mdi-chevron-left</v-icon>
					</div>

					<div class="workflow-step-card">
						<div class="step-badge-num">۵</div>
						<div class="step-icon-wrap">
							<v-icon color="#522ea8">mdi-credit-card-check-outline</v-icon>
						</div>
						<h4 class="step-title">پرداخت آنلاین و سابقه</h4>
						<p class="step-desc">پرداخت اینترنتی توسط مشتری و به‌روزرسانی رتبه اعتباری</p>
					</div>
				</div>
			</v-container>
		</section>

		<!-- ========================================================================= -->
		<!-- 6. PRICING & TARIFFS SECTION (تعرفه ها)                                    -->
		<!-- ========================================================================= -->
		<section id="pricing" class="landing-section pricing-hero-section">
			<div class="pricing-orb orb-p1"></div>
			<div class="pricing-orb orb-p2"></div>

			<v-container style="max-width: 1240px;" class="position-relative">
				<!-- Pricing Header -->
				<div class="text-center mb-12">
					<div class="pricing-chip mb-3">
						<v-icon small color="#ffb74d">mdi-tag-multiple-outline</v-icon>
						<span>تعرفه‌های شفاف و منصفانه</span>
					</div>
					<h2 class="pricing-title">پلن‌ها و تعرفه قیمت‌ها</h2>
					<p class="pricing-subtitle">پلن متناسب با حجم پرونده‌ها و نیازهای کسب‌وکار خود را انتخاب کنید</p>
				</div>

				<!-- Pricing Cards Grid -->
				<v-row class="justify-center align-stretch mb-12">
					<!-- Plan 1: Per Case -->
					<v-col cols="12" md="4">
						<div class="modern-price-card">
							<div class="price-card-header">
								<span class="plan-badge">شروع سریع</span>
								<h3 class="plan-name">پرونده‌ای سالیانه</h3>
								<div class="plan-type">سرویس‌دهی ابری (Cloud)</div>
							</div>

							<div class="price-value-box">
								<div class="price-sub">هر پرونده فروش اقساطی</div>
								<div class="price-amount">{{ cloudPrice ? cloudPrice.toString().toPriceFormat() : '...' }}</div>
								<div class="price-unit">تومان / سالانه</div>
							</div>

							<div class="plan-features-list">
								<div class="feature-line">
									<v-icon small color="#4caf50">mdi-check-circle</v-icon>
									<span>ماژول استعلام و ثبت پرونده اعتباری</span>
								</div>
								<div class="feature-line">
									<v-icon small color="#4caf50">mdi-check-circle</v-icon>
									<span>اتصال به دامنه اختصاصی شما</span>
								</div>
								<div class="feature-line">
									<v-icon small color="#4caf50">mdi-check-circle</v-icon>
									<span>سیستم خودکار پیامک‌های یادآوری</span>
								</div>
								<div class="feature-line">
									<v-icon small color="#4caf50">mdi-check-circle</v-icon>
									<span>درگاه پرداخت آنلاین شتابی</span>
								</div>
								<div class="feature-line">
									<v-icon small color="#4caf50">mdi-check-circle</v-icon>
									<span>۱۰ روز مهلت تست و بررسی رایگان</span>
								</div>
							</div>

							<div class="price-card-action">
								<v-btn v-if="isAdmin" block rounded large color="#ff9800" dark :loading="loading" @click="purchase(1)">
									ثبت خرید این پلن
								</v-btn>
								<v-btn v-else block rounded large color="#522ea8" dark @click="scrollTo('test-register')">
									تست رایگان این پلن
								</v-btn>
							</div>
						</div>
					</v-col>

					<!-- Plan 2: Unlimited Cloud (Recommended) -->
					<v-col cols="12" md="4">
						<div class="modern-price-card featured-price-card">
							<div class="featured-ribbon">
								<span>پیشنهاد ویژه • محبوب‌ترین</span>
							</div>

							<div class="price-card-header">
								<span class="plan-badge badge-featured">حرفه‌ای و بدون محدودیت</span>
								<h3 class="plan-name">نامحدود سالیانه</h3>
								<div class="plan-type">سرویس‌دهی ابری (Cloud)</div>
							</div>

							<div class="price-value-box">
								<div class="price-sub">تعداد پرونده نامحدود</div>
								<div class="price-amount">{{ permanentPrice ? permanentPrice.toString().toPriceFormat() : '...' }}</div>
								<div class="price-unit">تومان / سالانه</div>
							</div>

							<div class="plan-features-list">
								<div class="feature-line">
									<v-icon small color="#4caf50">mdi-check-circle</v-icon>
									<span><strong>ثبت نامحدود</strong> پرونده، مشتری و وام</span>
								</div>
								<div class="feature-line">
									<v-icon small color="#4caf50">mdi-check-circle</v-icon>
									<span>ماژول کامل استعلام و اعتبارسنجی</span>
								</div>
								<div class="feature-line">
									<v-icon small color="#4caf50">mdi-check-circle</v-icon>
									<span>اتصال به دامنه اختصاصی فروشگاه شما</span>
								</div>
								<div class="feature-line">
									<v-icon small color="#4caf50">mdi-check-circle</v-icon>
									<span>ارسال خودکار پیامک‌های زمان‌بندی‌شده</span>
								</div>
								<div class="feature-line">
									<v-icon small color="#4caf50">mdi-check-circle</v-icon>
									<span>درگاه آنلاین بانکی اختصاصی مشتریان</span>
								</div>
								<div class="feature-line">
									<v-icon small color="#4caf50">mdi-check-circle</v-icon>
									<span>اعتبار یکساله با پشتیبانی VIP</span>
								</div>
							</div>

							<div class="price-card-action">
								<v-btn v-if="isAdmin" block rounded large color="#ff9800" dark elevation="4" :loading="loading" class="font-weight-black" @click="purchase(0)">
									خرید پلن نامحدود
								</v-btn>
								<v-btn v-else block rounded large color="#ff9800" dark elevation="4" class="font-weight-black" @click="scrollTo('test-register')">
									شروع تست رایگان پلن
								</v-btn>
							</div>
						</div>
					</v-col>

					<!-- Plan 3: Custom On-Premise -->
					<v-col cols="12" md="4">
						<div class="modern-price-card">
							<div class="price-card-header">
								<span class="plan-badge">سازمانی و دائمی</span>
								<h3 class="plan-name">دائمی سفارشی</h3>
								<div class="plan-type">نصب بر روی سرور اختصاصی</div>
							</div>

							<div class="price-value-box">
								<div class="price-sub">مالکیت دائمی نرم‌افزار</div>
								<div class="price-amount fs-h3 mt-2">تماس با فروش</div>
								<div class="price-unit">بر اساس سفارشی‌سازی و سرور</div>
							</div>

							<div class="plan-features-list">
								<div class="feature-line">
									<v-icon small color="#4caf50">mdi-check-circle</v-icon>
									<span>نصب کامل بر روی سرور یا هاست اختصاصی</span>
								</div>
								<div class="feature-line">
									<v-icon small color="#4caf50">mdi-check-circle</v-icon>
									<span>لایسنس نامحدود زمانی بدون تاریخ انقضا</span>
								</div>
								<div class="feature-line">
									<v-icon small color="#4caf50">mdi-check-circle</v-icon>
									<span>سفارشی‌سازی فرمول‌ها و گزارش‌ها طبق نیاز</span>
								</div>
								<div class="feature-line">
									<v-icon small color="#4caf50">mdi-check-circle</v-icon>
									<span>پشتیبانی فنی و آموزش کامل پرسنل</span>
								</div>
								<div class="feature-line">
									<v-icon small color="#4caf50">mdi-check-circle</v-icon>
									<span>اتصال به سرویس‌های استعلامی سازمانی</span>
								</div>
							</div>

							<div class="price-card-action">
								<v-btn block rounded large outlined color="#ffffff" dark :href="whatsappUrl" target="_blank">
									<v-icon left>mdi-whatsapp</v-icon>
									مشاوره خرید در واتساپ
								</v-btn>
							</div>
						</div>
					</v-col>
				</v-row>
			</v-container>
		</section>

		<!-- ========================================================================= -->
		<!-- 7. TEST DRIVE SECTION (تست قبل از خرید - ۱۰ روز رایگان)                    -->
		<!-- ========================================================================= -->
		<section id="test-register" class="landing-section bg-soft-lilac">
			<v-container style="max-width: 1240px;">
				<div class="test-drive-card">
					<v-row class="align-center">
						<v-col cols="12" md="6" class="pl-md-8">
							<div class="test-drive-intro">
								<div class="d-flex align-center mb-3">
									<div class="test-icon-bubble ml-3">
										<v-icon color="#522ea8">mdi-shield-check</v-icon>
									</div>
									<div>
										<span class="free-pill-tag">۱۰ روز کاملاً رایگان</span>
										<h3 class="test-title">اول نرم‌افزار را تست کن، بعد تصمیم بگیر!</h3>
									</div>
								</div>
								<p class="test-desc">
									شماره موبایل خود را وارد نمایید تا رمز عبور ورود به سامانه بلافاصله برای شما پیامک شود. می‌توانید به مدت ۱۰ روز کلیه بخش‌ها از جمله تعریف وام، استعلام‌ها و صدور اقساط را تست و ارزیابی فرمایید.
								</p>

								<div class="test-steps-list">
									<div class="step-line">
										<span class="step-num">۱</span>
										<span>نام شخصی یا فروشگاه خود را به همراه شماره موبایل ثبت کنید.</span>
									</div>
									<div class="step-line">
										<span class="step-num">۲</span>
										<span>کلمه عبور آنی پیامک شده را دریافت فرمایید.</span>
									</div>
									<div class="step-line">
										<span class="step-num">۳</span>
										<span>وارد پنل شوید و دمو را با اطلاعات واقعی بررسی کنید.</span>
									</div>
								</div>

								<!-- Direct WhatsApp link in test drive section -->
								<div class="mt-6 pt-4 border-top-subtle d-flex align-center">
									<span class="grey--text text--darken-2 text-caption ml-2">نیاز به راهنمایی تلفنی یا مشاوره دارید؟</span>
									<a :href="whatsappUrl" target="_blank" class="whatsapp-text-link">
										<v-icon small color="#25D366" class="ml-1">mdi-whatsapp</v-icon>
										چت مستقیم در واتساپ
									</a>
								</div>
							</div>
						</v-col>

						<v-col cols="12" md="6">
							<div class="test-form-box">
								<v-form ref="registerForm" @submit.prevent="submitRegister">
									<v-row dense>
										<v-col cols="12">
											<v-text-field ref="regName" dark filled rounded v-model="reg.name" label="نام و نام خانوادگی یا عنوان کسب‌وکار" prepend-inner-icon="mdi-account-outline" :error-messages="errorMessages" :rules="[(value) => !!value || 'لطفاً نام را وارد کنید']" required />
										</v-col>

										<v-col cols="12">
											<v-text-field ref="regMobile" maxlength="11" dark filled rounded v-model="reg.mobile" label="شماره موبایل (مثال: ۰۹۱۲۳۴۵۶۷۸۹)" prepend-inner-icon="mdi-cellphone" :error-messages="errorMessages" :rules="[
												(value) => !!value || 'لطفاً شماره موبایل را وارد کنید',
												(value) => isPhoneNumberValid() || 'شماره موبایل نامعتبر است'
											]" required />
										</v-col>

										<v-col cols="12" class="text-center mt-2">
											<Countdown class="white--text fs-h3 mb-2" @finish="countDownFinished" ref="countdown" />

											<v-btn v-if="sendCodeVisible" :loading="regLoading" block rounded large color="#ff9800" dark type="submit" class="font-weight-black submit-test-btn">
												<v-icon left>mdi-send-lock-outline</v-icon>
												دریافت رمز عبور پیامکی
											</v-btn>

											<div v-if="showLoginToAppBtn" class="login-prompt-box pa-4 rounded-xl mt-3">
												<div class="white--text mb-2">رمز عبور با موفقیت برای شما ارسال شد.</div>
												<v-btn block rounded large color="#ffffff" class="font-weight-black brand-purple-text" @click.prevent="loginToApp">
													<v-icon left color="#522ea8">mdi-login</v-icon>
													ورود مستقیم به نرم‌افزار
												</v-btn>
											</div>

											<div v-show="regValidation" class="error-validation-box pa-3 rounded-lg mt-3">
												<v-icon small color="white" class="ml-1">mdi-alert-circle</v-icon>
												{{ regValidation }}
											</div>
										</v-col>
									</v-row>
								</v-form>
							</div>
						</v-col>
					</v-row>
				</div>
			</v-container>
		</section>

		<!-- ========================================================================= -->
		<!-- FLOATING QUICK WHATSAPP BUTTON WITH PULSE MICROINTERACTION                -->
		<!-- ========================================================================= -->
		<a :href="whatsappUrl" target="_blank" class="floating-whatsapp-trigger" title="مشاوره رایگان در واتساپ">
			<span class="whatsapp-pulse-ring"></span>
			<span class="whatsapp-pulse-ring delay-1"></span>
			<v-icon color="#ffffff" size="32">mdi-whatsapp</v-icon>
			<span class="floating-tooltip">مشاوره رایگان در واتساپ</span>
		</a>
	</div>
</template>

<script>
import { get } from 'vuex-pathify'
import api from '@/util/app-api'
import toast from '@/util/toast'
import router from '@/router'
import { getCookie } from '@/util/helpers'

export default {
	name: 'CreditInquiryLandingView',

	components: {
		Countdown: () => import('@/components/Countdown'),
	},

	data: () => ({
		errorMessages: '',
		reg: { name: '', mobile: '' },
		loading: false,
		regLoading: false,
		regValidation: '',
		sendCodeVisible: true,
		showLoginToAppBtn: false,

		// Mouse move microinteraction state
		tiltX: 0,
		tiltY: 0,
		badgeX1: 0,
		badgeY1: 0,
		badgeX2: 0,
		badgeY2: 0,
		badgeX3: 0,
		badgeY3: 0,
		spotlightX: 50,
		spotlightY: 50,

		whatsappPhone: '989361995434',
		whatsappMessage: 'سلام، برای اعتبارسنجی و استعلام آنلاین در سامانه جت اقساط درخواست مشاوره و دمو داشتم.'
	}),

	computed: {
		...get('auth', [
			'isAdmin', 'getCurrentUser'
		]),
		...get('siteConfig', [
			'permanentPrice', 'cloudPrice'
		]),
		whatsappUrl() {
			return `https://wa.me/${this.whatsappPhone}?text=${encodeURIComponent(this.whatsappMessage)}`
		}
	},

	mounted() {
		if (this.$route.hash) {
			const sectionId = this.$route.hash.replace('#', '')
			this.$nextTick(() => {
				setTimeout(() => {
					this.scrollTo(sectionId)
				}, 350)
			})
		}
	},

	watch: {
		'$route.hash'(newHash) {
			if (newHash) {
				const sectionId = newHash.replace('#', '')
				this.$nextTick(() => {
					setTimeout(() => {
						this.scrollTo(sectionId)
					}, 200)
				})
			}
		}
	},

	methods: {
		// Hero banner mouse move interaction
		onHeroMouseMove(e) {
			const banner = this.$refs.heroBanner
			if (!banner) return
			const rect = banner.getBoundingClientRect()
			const x = (e.clientX - rect.left) / rect.width - 0.5
			const y = (e.clientY - rect.top) / rect.height - 0.5

			this.tiltX = y * -12
			this.tiltY = x * 14

			this.badgeX1 = x * -22
			this.badgeY1 = y * -16

			this.badgeX2 = x * 26
			this.badgeY2 = y * 18

			this.badgeX3 = x * -18
			this.badgeY3 = y * 22

			this.spotlightX = Math.round(((e.clientX - rect.left) / rect.width) * 100)
			this.spotlightY = Math.round(((e.clientY - rect.top) / rect.height) * 100)
		},

		onHeroMouseLeave() {
			this.tiltX = 0
			this.tiltY = 0
			this.badgeX1 = 0
			this.badgeY1 = 0
			this.badgeX2 = 0
			this.badgeY2 = 0
			this.badgeX3 = 0
			this.badgeY3 = 0
			this.spotlightX = 50
			this.spotlightY = 50
		},

		scrollTo(sectionId) {
			const el = document.getElementById(sectionId)
			if (el) {
				const navOffset = 95
				const elementPosition = el.getBoundingClientRect().top
				const offsetPosition = elementPosition + window.pageYOffset - navOffset
				window.scrollTo({
					top: offsetPosition,
					behavior: 'smooth'
				})

				if (window.history && window.history.pushState) {
					window.history.pushState(null, null, `#${sectionId}`)
				}
			}
		},

		purchase(productType) {
			this.loading = true
			let data = {
				productType: productType,
				paymentMethod: 0
			}
			api.post('admin/purchase/create', data)
				.then(response => {
					window.location.replace(response)
				})
				.catch(err => {
					this.loading = false
					toast.error(err.message)
				})
		},

		submitRegister() {
			if (!this.$refs.registerForm.validate()) { return }
			this.regValidation = ''
			this.regLoading = true

			const formData = new FormData()
			formData.append('name', this.reg.name)
			formData.append('mobile', this.fixPhoneNumber(this.reg.mobile))
			formData.append('sendSms', true)
			const affCode = getCookie('aff')
			if (affCode) {
				formData.append('affCode', affCode)
			}
			api.post('account/register', formData, { authHeader: false, contentType: 'multipart/form-data' })
				.then(() => {
					if (this.$refs.countdown && this.$refs.countdown.start) {
						this.$refs.countdown.start()
					}
					this.sendCodeVisible = false
					this.showLoginToAppBtn = true
				})
				.catch(err => {
					this.regValidation = err.message
				})
				.finally(() => {
					this.regLoading = false
				})
		},

		countDownFinished() {
			this.sendCodeVisible = true
		},

		isPhoneNumberValid() {
			return this.reg.mobile && (/^((?!(0))[0-9]{10})$/g.test(this.reg.mobile) || /^0[0-9]\d{9}$/g.test(this.reg.mobile))
		},

		loginToApp() {
			router.push('/login/0/?from=home').catch(() => { })
		},

		fixPhoneNumber(number) {
			if (number?.length === 10 && !number.startsWith('0'))
				number = '0' + number
			return number
		}
	}
}
</script>

<style lang="scss">
.credit-landing-root {
	font-family: "iranyekanweb", "Emkan-Regular", "Yekan", tahoma, sans-serif;
	color: #2b233a;
	overflow-x: hidden;
}

.landing-section {
	padding: 70px 0;
	position: relative;
}

.bg-soft-lilac {
	background: #f8f6fc;
}

.border-bottom-subtle {
	border-bottom: 1px solid rgba(82, 46, 168, 0.08);
}

.border-top-subtle {
	border-top: 1px solid rgba(82, 46, 168, 0.12);
}

.max-w-700 {
	max-width: 700px;
}

.color-dark {
	color: #241442;
}

.brand-purple-text {
	color: #522ea8 !important;
}

/* ========================================================================= */
/* 1. HERO SECTION STYLES                                                    */
/* ========================================================================= */
.credit-hero-section {
	width: 100%;
	position: relative;
	overflow: hidden;
	background: linear-gradient(135deg, #ffffff 0%, #f6f3fc 35%, #efe7f9 70%, #ffffff 100%);
	border-bottom: 1px solid rgba(82, 46, 168, 0.08);
	min-height: 620px;
	display: flex;
	align-items: center;
}

.hero-spotlight-glow {
	position: absolute;
	inset: 0;
	pointer-events: none;
	transition: background 0.15s ease-out;
}

.ambient-orb {
	position: absolute;
	border-radius: 50%;
	filter: blur(85px);
	pointer-events: none;
	opacity: 0.6;
}

.orb-purple {
	width: 440px;
	height: 440px;
	background: rgba(110, 65, 220, 0.14);
	top: -100px;
	right: -100px;
}

.orb-emerald {
	width: 380px;
	height: 380px;
	background: rgba(37, 211, 102, 0.08);
	bottom: -50px;
	left: 10%;
}

.ambient-grid-pattern {
	position: absolute;
	inset: 0;
	background-image: radial-gradient(rgba(82, 46, 168, 0.08) 1.5px, transparent 1.5px);
	background-size: 24px 24px;
	opacity: 0.5;
	pointer-events: none;
}

.hero-container {
	position: relative;
	z-index: 2;
	max-width: 1240px;
}

.hero-chip {
	display: inline-flex;
	align-items: center;
	padding: 6px 16px;
	border-radius: 50px;
	background: rgba(82, 46, 168, 0.08);
	border: 1px solid rgba(82, 46, 168, 0.18);
	color: #522ea8;
	font-size: 0.84rem;
	font-weight: 700;
}

.hero-pulse-dot {
	width: 8px;
	height: 8px;
	border-radius: 50%;
	background-color: #25D366;
	margin-left: 6px;
	box-shadow: 0 0 0 0 rgba(37, 211, 102, 0.7);
	animation: pulseEmerald 2s infinite;
}

@keyframes pulseEmerald {
	0% {
		transform: scale(0.95);
		box-shadow: 0 0 0 0 rgba(37, 211, 102, 0.7);
	}

	70% {
		transform: scale(1);
		box-shadow: 0 0 0 6px rgba(37, 211, 102, 0);
	}

	100% {
		transform: scale(0.95);
		box-shadow: 0 0 0 0 rgba(37, 211, 102, 0);
	}
}

.hero-title {
	font-size: 2.3rem;
	font-weight: 900;
	line-height: 1.45;
	color: #241442;
	font-family: "Emkan-Bold", "iranyekanweb", sans-serif;
}

.hero-title-highlight {
	color: #522ea8;
	position: relative;
	display: inline-block;

	&::after {
		content: "";
		position: absolute;
		bottom: 4px;
		left: 0;
		right: 0;
		height: 8px;
		background: rgba(82, 46, 168, 0.15);
		border-radius: 4px;
		z-index: -1;
	}
}

.hero-subtitle {
	font-size: 1.05rem;
	line-height: 2.1;
	color: #554a6b;
	text-align: justify;
}

.hero-actions {
	display: flex;
	align-items: center;
	flex-wrap: wrap;

	.hero-btn-primary {
		background: linear-gradient(135deg, #522ea8 0%, #6e41dc 100%) !important;
		color: #ffffff !important;
		font-weight: 800;
		font-size: 1rem;
		padding: 0 28px !important;
		height: 52px !important;
		box-shadow: 0 8px 24px rgba(82, 46, 168, 0.35) !important;
		transition: all 0.3s ease !important;

		&:hover {
			transform: translateY(-3px);
			box-shadow: 0 12px 30px rgba(82, 46, 168, 0.45) !important;
		}
	}

	.hero-btn-whatsapp {
		background: linear-gradient(135deg, #25D366 0%, #1ea952 100%) !important;
		color: #ffffff !important;
		font-weight: 800;
		font-size: 1rem;
		padding: 0 26px !important;
		height: 52px !important;
		box-shadow: 0 8px 24px rgba(37, 211, 102, 0.35) !important;
		transition: all 0.3s ease !important;

		&:hover {
			transform: translateY(-3px);
			box-shadow: 0 12px 30px rgba(37, 211, 102, 0.5) !important;
		}
	}
}

.hero-trust-row {
	display: flex;
	align-items: center;
	flex-wrap: wrap;
	gap: 16px;
	padding-top: 14px;
	border-top: 1px dashed rgba(82, 46, 168, 0.18);

	.trust-item {
		display: flex;
		align-items: center;
		gap: 6px;
		font-size: 0.86rem;
		font-weight: 600;
		color: #594d73;
	}
}

/* Hero Right (3D Card & Inquiry Report Mockup) */
.hero-visual-wrapper {
	position: relative;
	display: flex;
	justify-content: center;
	align-items: center;
	padding: 24px 12px;
}

.hero-3d-card {
	width: 100%;
	max-width: 530px;
	background: #ffffff;
	border-radius: 22px;
	overflow: hidden;
	box-shadow: 0 24px 60px -15px rgba(82, 46, 168, 0.22), 0 0 1px 1px rgba(82, 46, 168, 0.1);
	border: 1px solid rgba(82, 46, 168, 0.15);
	transition: transform 0.15s ease-out, box-shadow 0.2s ease-out;
	will-change: transform;
}

.mockup-header-bar {
	background: #f7f5fa;
	padding: 10px 16px;
	display: flex;
	align-items: center;
	justify-content: space-between;
	border-bottom: 1px solid rgba(0, 0, 0, 0.06);

	.mockup-dots {
		display: flex;
		gap: 6px;

		.m-dot {
			width: 10px;
			height: 10px;
			border-radius: 50%;
		}

		.dot-red {
			background: #ff5f56;
		}

		.dot-yellow {
			background: #ffbd2e;
		}

		.dot-green {
			background: #27c93f;
		}
	}

	.mockup-title {
		font-size: 0.75rem;
		color: #6d6287;
		font-weight: 600;
		display: flex;
		align-items: center;
	}

	.mockup-status-tag {
		display: flex;
		align-items: center;
		gap: 4px;
		font-size: 0.7rem;
		font-weight: 700;
		color: #2e7d32;

		.status-pulse-dot {
			width: 6px;
			height: 6px;
			border-radius: 50%;
			background: #2e7d32;
		}
	}
}

.inquiry-report-mockup {
	background: #ffffff;

	.customer-avatar-box {
		width: 44px;
		height: 44px;
		border-radius: 12px;
		background: rgba(82, 46, 168, 0.1);
		display: flex;
		align-items: center;
		justify-content: center;
	}

	.metric-card {
		border: 1px solid rgba(0, 0, 0, 0.05);

		&.bg-soft-green {
			background: #f1f8e9;
		}

		&.bg-soft-purple {
			background: #f3e5f5;
		}
	}

	.recommendation-bar {
		background: #f9f8fc;
		border: 1px solid rgba(82, 46, 168, 0.12);
	}
}

/* Floating Parallax Badges */
.parallax-badge {
	position: absolute;
	display: flex;
	align-items: center;
	padding: 10px 14px;
	background: rgba(255, 255, 255, 0.94);
	backdrop-filter: blur(14px);
	-webkit-backdrop-filter: blur(14px);
	border-radius: 16px;
	border: 1px solid rgba(82, 46, 168, 0.15);
	box-shadow: 0 12px 30px rgba(82, 46, 168, 0.18), 0 2px 8px rgba(0, 0, 0, 0.04);
	z-index: 5;
	transition: transform 0.2s ease-out;
	user-select: none;

	.badge-icon-box {
		width: 36px;
		height: 36px;
		border-radius: 10px;
		display: flex;
		align-items: center;
		justify-content: center;
		margin-left: 10px;

		&.bg-purple {
			background: rgba(82, 46, 168, 0.12);
		}

		&.bg-emerald {
			background: rgba(37, 211, 102, 0.14);
		}

		&.bg-amber {
			background: rgba(255, 152, 0, 0.14);
		}
	}

	.badge-text-box {
		display: flex;
		flex-direction: column;

		.badge-primary {
			font-size: 0.8rem;
			font-weight: 800;
			color: #271a3e;
			line-height: 1.2;
		}

		.badge-secondary {
			font-size: 0.68rem;
			color: #71658b;
			margin-top: 1px;
		}
	}

	&.badge-check {
		top: 10px;
		left: -15px;
	}

	&.badge-risk {
		bottom: 25px;
		right: -15px;
	}

	&.badge-speed {
		bottom: -15px;
		left: 15px;
	}
}

/* ========================================================================= */
/* 2. DEDICATED APARAT VIDEO SECTION STYLES                                  */
/* ========================================================================= */
.section-badge-header {
	.section-chip {
		display: inline-flex;
		align-items: center;
		gap: 6px;
		padding: 5px 14px;
		border-radius: 50px;
		background: rgba(255, 152, 0, 0.1);
		color: #e65100;
		font-size: 0.82rem;
		font-weight: 700;
	}

	.section-main-heading {
		font-size: 2.1rem;
		font-weight: 900;
		color: #241442;
		font-family: "Emkan-Bold", "iranyekanweb", sans-serif;
		margin: 8px 0;
	}

	.section-sub-heading {
		font-size: 1rem;
		color: #6c5f87;
	}
}

.video-card-container {
	background: linear-gradient(135deg, #f7f5fb 0%, #ede6f8 100%);
	padding: 12px;
	border-radius: 26px;
	border: 1px solid rgba(82, 46, 168, 0.18);
	box-shadow: 0 16px 45px -10px rgba(82, 46, 168, 0.2);

	.video-card-inner {
		border-radius: 20px;
		overflow: hidden;
		background: #000;
	}

	.video-frame-wrapper {
		position: relative;
		width: 100%;
		padding-bottom: 56.25%;
		/* 16:9 Aspect Ratio */
		height: 0;

		iframe {
			position: absolute;
			top: 0;
			left: 0;
			width: 100%;
			height: 100%;
			border: 0;
		}
	}

	.video-footer-strip {
		background: #ffffff;
		padding: 12px 18px;
		display: flex;
		align-items: center;
		justify-content: space-between;

		.video-play-indicator {
			width: 26px;
			height: 26px;
			border-radius: 50%;
			background: #522ea8;
			display: flex;
			align-items: center;
			justify-content: center;
		}

		.video-title-text {
			font-size: 0.88rem;
			color: #352553;
		}

		.aparat-link-btn {
			display: inline-flex;
			align-items: center;
			font-size: 0.8rem;
			font-weight: 700;
			color: #e65100;
			text-decoration: none;
			padding: 4px 10px;
			border-radius: 8px;
			background: rgba(255, 152, 0, 0.1);
			transition: all 0.2s ease;

			&:hover {
				background: rgba(255, 152, 0, 0.2);
			}
		}
	}
}

.video-side-box {
	.video-side-title {
		font-size: 1.35rem;
		font-weight: 900;
		color: #241442;
	}

	.takeaway-card {
		display: flex;
		align-items: flex-start;
		padding: 14px;
		border-radius: 16px;
		background: #fbf9fd;
		border: 1px solid rgba(82, 46, 168, 0.08);
		transition: all 0.3s ease;

		&:hover {
			transform: translateY(-2px);
			background: #ffffff;
			box-shadow: 0 8px 24px rgba(82, 46, 168, 0.1);
			border-color: rgba(82, 46, 168, 0.2);
		}

		.takeaway-icon {
			width: 44px;
			height: 44px;
			border-radius: 12px;
			display: flex;
			align-items: center;
			justify-content: center;
			margin-left: 14px;
			flex-shrink: 0;

			&.bg-light-purple {
				background: rgba(82, 46, 168, 0.1);
			}

			&.bg-light-green {
				background: rgba(46, 125, 50, 0.1);
			}

			&.bg-light-amber {
				background: rgba(255, 152, 0, 0.12);
			}
		}

		.takeaway-text {
			h4 {
				font-size: 0.95rem;
				font-weight: 800;
				color: #291a42;
				margin-bottom: 3px;
			}

			p {
				font-size: 0.85rem;
				line-height: 1.7;
				color: #64587c;
				margin: 0;
			}
		}
	}

	.whatsapp-outline-btn {
		border: 2px solid #25D366 !important;
		color: #25D366 !important;
		transition: all 0.25s ease;

		&:hover {
			background: #25D366 !important;
			color: #ffffff !important;

			.v-icon {
				color: #ffffff !important;
			}
		}
	}
}

/* ========================================================================= */
/* 3. ESSENTIAL INQUIRY PILLARS                                              */
/* ========================================================================= */
.inquiry-pillar-card {
	background: #ffffff;
	border-radius: 20px;
	padding: 26px 20px;
	border: 1px solid rgba(82, 46, 168, 0.08);
	box-shadow: 0 8px 20px rgba(82, 46, 168, 0.05);
	transition: all 0.3s ease;
	height: 100%;

	&:hover {
		transform: translateY(-6px);
		box-shadow: 0 16px 35px rgba(82, 46, 168, 0.14);
		border-color: rgba(82, 46, 168, 0.2);

		.pillar-icon-box {
			transform: scale(1.08) rotate(-4deg);
		}
	}

	.pillar-icon-box {
		width: 56px;
		height: 56px;
		border-radius: 16px;
		display: flex;
		align-items: center;
		justify-content: center;
		margin-bottom: 16px;
		transition: all 0.3s ease;

		&.bg-purple {
			background: rgba(82, 46, 168, 0.1);
		}

		&.bg-red {
			background: rgba(211, 47, 47, 0.1);
		}

		&.bg-emerald {
			background: rgba(46, 125, 50, 0.1);
		}

		&.bg-amber {
			background: rgba(255, 152, 0, 0.12);
		}

		&.bg-indigo {
			background: rgba(48, 63, 159, 0.1);
		}
	}

	.pillar-title {
		font-size: 1.12rem;
		font-weight: 800;
		color: #271940;
		margin-bottom: 8px;
	}

	.pillar-desc {
		font-size: 0.88rem;
		color: #695e80;
		line-height: 1.8;
		margin: 0;
	}
}

/* ========================================================================= */
/* 4. SMART DECISION MATRIX (TRAFFIC LIGHT)                                  */
/* ========================================================================= */
.decision-card {
	border-radius: 24px;
	padding: 30px 24px;
	background: #ffffff;
	border: 2px solid transparent;
	box-shadow: 0 10px 30px rgba(0, 0, 0, 0.06);
	transition: all 0.35s ease;
	height: 100%;
	display: flex;
	flex-direction: column;
	justify-content: space-between;

	&:hover {
		transform: translateY(-8px);
	}

	&.card-green {
		border-color: #a5d6a7;
		background: linear-gradient(180deg, #ffffff 0%, #f1f8e9 100%);

		&:hover {
			box-shadow: 0 20px 45px rgba(46, 125, 50, 0.18);
		}
	}

	&.card-yellow {
		border-color: #ffe082;
		background: linear-gradient(180deg, #ffffff 0%, #fffde7 100%);

		&:hover {
			box-shadow: 0 20px 45px rgba(245, 127, 23, 0.18);
		}
	}

	&.card-red {
		border-color: #ef9a9a;
		background: linear-gradient(180deg, #ffffff 0%, #ffebee 100%);

		&:hover {
			box-shadow: 0 20px 45px rgba(198, 40, 40, 0.18);
		}
	}

	.decision-badge {
		display: inline-flex;
		align-items: center;
		padding: 5px 12px;
		border-radius: 50px;
		font-size: 0.78rem;
		font-weight: 800;
		margin-bottom: 12px;
		align-self: flex-start;

		&.badge-green {
			background: #e8f5e9;
			color: #2e7d32;
		}

		&.badge-yellow {
			background: #fff8e1;
			color: #f57f17;
		}

		&.badge-red {
			background: #ffebee;
			color: #c62828;
		}
	}

	.decision-title {
		font-size: 1.25rem;
		font-weight: 900;
		color: #241442;
		margin-bottom: 8px;
	}

	.decision-desc {
		font-size: 0.9rem;
		color: #5d5276;
		line-height: 1.8;
		margin-bottom: 18px;
	}

	.decision-action-box {
		background: rgba(255, 255, 255, 0.85);
		padding: 14px;
		border-radius: 14px;
		border: 1px solid rgba(0, 0, 0, 0.06);

		.action-label {
			font-size: 0.75rem;
			font-weight: 800;
			color: #7a7092;
			display: block;
			margin-bottom: 4px;
		}

		.action-tag {
			font-size: 0.85rem;
			font-weight: 800;
			line-height: 1.5;

			&.text-green {
				color: #2e7d32;
			}

			&.text-amber {
				color: #e65100;
			}

			&.text-red {
				color: #c62828;
			}
		}
	}
}

/* ========================================================================= */
/* 5. WORKFLOW INTEGRATION                                                   */
/* ========================================================================= */
.workflow-steps-grid {
	display: flex;
	align-items: center;
	justify-content: space-between;
	flex-wrap: wrap;
	gap: 12px;

	.workflow-step-card {
		flex: 1;
		min-width: 180px;
		background: #ffffff;
		border-radius: 20px;
		padding: 24px 16px;
		text-align: center;
		border: 1px solid rgba(82, 46, 168, 0.1);
		box-shadow: 0 8px 24px rgba(82, 46, 168, 0.06);
		position: relative;
		transition: all 0.3s ease;

		&:hover,
		&.active-step {
			transform: translateY(-5px);
			border-color: #522ea8;
			box-shadow: 0 16px 35px rgba(82, 46, 168, 0.18);
		}

		.step-badge-num {
			position: absolute;
			top: -12px;
			right: 18px;
			width: 26px;
			height: 26px;
			border-radius: 50%;
			background: #522ea8;
			color: #ffffff;
			font-weight: 900;
			font-size: 0.8rem;
			display: flex;
			align-items: center;
			justify-content: center;
		}

		.step-icon-wrap {
			width: 52px;
			height: 52px;
			border-radius: 16px;
			background: rgba(82, 46, 168, 0.08);
			display: flex;
			align-items: center;
			justify-content: center;
			margin: 0 auto 12px;
		}

		.step-title {
			font-size: 0.98rem;
			font-weight: 800;
			color: #241442;
			margin-bottom: 6px;
		}

		.step-desc {
			font-size: 0.78rem;
			color: #695e80;
			line-height: 1.6;
			margin: 0;
		}
	}

	.workflow-connector {
		display: flex;
		align-items: center;
		justify-content: center;
		opacity: 0.5;
	}
}

@media (max-width: 959px) {
	.workflow-connector {
		display: none;
	}

	.workflow-step-card {
		min-width: 100% !important;
		margin-bottom: 12px;
	}
}

/* ========================================================================= */
/* 6. PRICING STYLES                                                         */
/* ========================================================================= */
.pricing-hero-section {
	background: linear-gradient(145deg, #1d0f36 0%, #2f175a 50%, #170b2d 100%);
	position: relative;
	overflow: hidden;
	padding: 85px 0;
	color: #ffffff;
}

.pricing-orb {
	position: absolute;
	border-radius: 50%;
	filter: blur(90px);
	pointer-events: none;
}

.orb-p1 {
	width: 450px;
	height: 450px;
	background: rgba(110, 65, 220, 0.2);
	top: -80px;
	left: 10%;
}

.orb-p2 {
	width: 400px;
	height: 400px;
	background: rgba(37, 211, 102, 0.1);
	bottom: 10%;
	right: 5%;
}

.pricing-chip {
	display: inline-flex;
	align-items: center;
	gap: 6px;
	padding: 5px 16px;
	border-radius: 50px;
	background: rgba(255, 183, 77, 0.15);
	color: #ffb74d;
	font-size: 0.85rem;
	font-weight: 700;
}

.pricing-title {
	font-size: 2.2rem;
	font-weight: 900;
	color: #ffffff;
	font-family: "Emkan-Bold", "iranyekanweb", sans-serif;
}

.pricing-subtitle {
	font-size: 1rem;
	color: rgba(255, 255, 255, 0.7);
}

.modern-price-card {
	background: rgba(255, 255, 255, 0.06);
	backdrop-filter: blur(14px);
	-webkit-backdrop-filter: blur(14px);
	border: 1px solid rgba(255, 255, 255, 0.12);
	border-radius: 26px;
	padding: 32px 24px;
	display: flex;
	flex-direction: column;
	justify-content: space-between;
	position: relative;
	transition: all 0.35s ease;
	height: 100%;

	&:hover {
		transform: translateY(-8px);
		border-color: rgba(255, 255, 255, 0.25);
		background: rgba(255, 255, 255, 0.09);
		box-shadow: 0 20px 45px rgba(0, 0, 0, 0.3);
	}

	&.featured-price-card {
		background: rgba(255, 255, 255, 0.1);
		border: 2px solid #ff9800;
		box-shadow: 0 20px 50px rgba(0, 0, 0, 0.35);

		.featured-ribbon {
			position: absolute;
			top: -14px;
			right: 24px;
			background: linear-gradient(135deg, #ff9800 0%, #f57c00 100%);
			color: #ffffff;
			font-size: 0.75rem;
			font-weight: 900;
			padding: 4px 14px;
			border-radius: 20px;
			box-shadow: 0 4px 12px rgba(245, 124, 0, 0.4);
		}
	}

	.plan-badge {
		display: inline-block;
		padding: 4px 12px;
		border-radius: 20px;
		background: rgba(255, 255, 255, 0.1);
		font-size: 0.78rem;
		color: rgba(255, 255, 255, 0.85);
		margin-bottom: 8px;

		&.badge-featured {
			background: rgba(255, 152, 0, 0.2);
			color: #ffb74d;
		}
	}

	.plan-name {
		font-size: 1.45rem;
		font-weight: 900;
		color: #ffffff;
		margin-bottom: 4px;
	}

	.plan-type {
		font-size: 0.84rem;
		color: rgba(255, 255, 255, 0.6);
	}

	.price-value-box {
		margin: 24px 0;
		padding: 16px;
		border-radius: 16px;
		background: rgba(0, 0, 0, 0.2);
		text-align: center;

		.price-sub {
			font-size: 0.82rem;
			color: rgba(255, 255, 255, 0.65);
		}

		.price-amount {
			font-size: 2.2rem;
			font-weight: 900;
			color: #ffffff;
			font-family: "yekan", sans-serif;
		}

		.price-unit {
			font-size: 0.82rem;
			color: rgba(255, 255, 255, 0.65);
		}
	}

	.plan-features-list {
		display: flex;
		flex-direction: column;
		gap: 12px;
		margin-bottom: 28px;

		.feature-line {
			display: flex;
			align-items: center;
			gap: 10px;
			font-size: 0.88rem;
			color: rgba(255, 255, 255, 0.88);
		}
	}
}

/* ========================================================================= */
/* 7. TEST DRIVE SECTION                                                     */
/* ========================================================================= */
.test-drive-card {
	background: #ffffff;
	border-radius: 28px;
	border: 1px solid rgba(82, 46, 168, 0.12);
	padding: 36px 30px;
	box-shadow: 0 16px 45px rgba(82, 46, 168, 0.1);

	.test-icon-bubble {
		width: 48px;
		height: 48px;
		border-radius: 14px;
		background: rgba(82, 46, 168, 0.1);
		display: flex;
		align-items: center;
		justify-content: center;
	}

	.free-pill-tag {
		font-size: 0.75rem;
		font-weight: 800;
		color: #2e7d32;
		background: #e8f5e9;
		padding: 2px 10px;
		border-radius: 12px;
	}

	.test-title {
		font-size: 1.35rem;
		font-weight: 900;
		color: #241442;
		margin-top: 4px;
	}

	.test-desc {
		font-size: 0.95rem;
		color: #5d5276;
		line-height: 1.9;
	}

	.test-steps-list {
		display: flex;
		flex-direction: column;
		gap: 12px;
		margin-top: 18px;

		.step-line {
			display: flex;
			align-items: center;
			gap: 12px;
			font-size: 0.9rem;
			color: #3f3358;
			font-weight: 600;

			.step-num {
				width: 24px;
				height: 24px;
				border-radius: 50%;
				background: #522ea8;
				color: #ffffff;
				display: flex;
				align-items: center;
				justify-content: center;
				font-weight: 800;
				font-size: 0.8rem;
				flex-shrink: 0;
			}
		}
	}

	.whatsapp-text-link {
		color: #2e7d32;
		font-weight: 800;
		text-decoration: none;
		display: inline-flex;
		align-items: center;
		font-size: 0.88rem;

		&:hover {
			text-decoration: underline;
		}
	}

	.test-form-box {
		background: linear-gradient(145deg, #241442 0%, #361b68 100%);
		padding: 28px;
		border-radius: 24px;
		box-shadow: 0 12px 35px rgba(36, 20, 66, 0.35);

		.v-input__slot {
			background: rgba(14, 7, 28, 0.6) !important;
			border: 1px solid rgba(255, 255, 255, 0.14) !important;
			transition: border-color 0.25s ease;

			&:hover {
				border-color: rgba(110, 65, 220, 0.6) !important;
			}
		}

		.submit-test-btn {
			background: linear-gradient(135deg, #ff9800 0%, #f57c00 100%) !important;
			box-shadow: 0 6px 20px rgba(245, 124, 0, 0.35) !important;
		}

		.login-prompt-box {
			background: rgba(82, 46, 168, 0.5);
			border: 1px solid rgba(255, 255, 255, 0.25);
		}

		.error-validation-box {
			background: rgba(233, 30, 99, 0.9);
			color: #ffffff;
			font-size: 0.88rem;
		}
	}
}

/* ========================================================================= */
/* 8. WHATSAPP SPOTLIGHT BANNER                                              */
/* ========================================================================= */
.whatsapp-spotlight-section {
	padding: 40px 0 70px;
}

.whatsapp-banner-card {
	background: linear-gradient(135deg, #1b0f33 0%, #29154f 50%, #0d4a25 100%);
	border-radius: 28px;
	border: 1px solid rgba(37, 211, 102, 0.3);
	box-shadow: 0 20px 50px rgba(0, 0, 0, 0.3);

	.whatsapp-badge-icon {
		width: 52px;
		height: 52px;
		border-radius: 16px;
		background: #25D366;
		display: flex;
		align-items: center;
		justify-content: center;
		box-shadow: 0 4px 15px rgba(37, 211, 102, 0.4);
	}

	.whatsapp-chat-action-btn {
		background: linear-gradient(135deg, #25D366 0%, #1ea952 100%) !important;
		color: #ffffff !important;
		font-size: 1.05rem;
		padding: 0 32px !important;
		height: 56px !important;
		box-shadow: 0 8px 25px rgba(37, 211, 102, 0.4) !important;
		transition: all 0.3s ease !important;

		&:hover {
			transform: translateY(-3px);
			box-shadow: 0 12px 32px rgba(37, 211, 102, 0.55) !important;
		}
	}
}

/* ========================================================================= */
/* FLOATING QUICK WHATSAPP BUTTON                                            */
/* ========================================================================= */
.floating-whatsapp-trigger {
	position: fixed;
	bottom: 28px;
	left: 28px;
	width: 62px;
	height: 62px;
	border-radius: 50%;
	background: linear-gradient(135deg, #25D366 0%, #128C7E 100%);
	box-shadow: 0 8px 25px rgba(37, 211, 102, 0.5);
	display: flex;
	align-items: center;
	justify-content: center;
	z-index: 999;
	text-decoration: none;
	transition: all 0.3s ease;

	&:hover {
		transform: scale(1.08) translateY(-4px);
		box-shadow: 0 14px 35px rgba(37, 211, 102, 0.65);

		.floating-tooltip {
			opacity: 1;
			visibility: visible;
			transform: translateX(0);
		}
	}

	.floating-tooltip {
		position: absolute;
		right: 74px;
		background: #241442;
		color: #ffffff;
		padding: 6px 14px;
		border-radius: 12px;
		font-size: 0.8rem;
		font-weight: 700;
		white-space: nowrap;
		box-shadow: 0 4px 15px rgba(0, 0, 0, 0.2);
		opacity: 0;
		visibility: hidden;
		transform: translateX(-10px);
		transition: all 0.25s ease;
		pointer-events: none;

		&::after {
			content: "";
			position: absolute;
			top: 50%;
			left: 100%;
			margin-top: -5px;
			border-width: 5px;
			border-style: solid;
			border-color: transparent transparent transparent #241442;
		}
	}

	.whatsapp-pulse-ring {
		position: absolute;
		inset: -4px;
		border-radius: 50%;
		border: 2px solid #25D366;
		animation: waPulseRing 2.4s cubic-bezier(0.215, 0.61, 0.355, 1) infinite;

		&.delay-1 {
			animation-delay: 1.2s;
		}
	}
}

@keyframes waPulseRing {
	0% {
		transform: scale(0.9);
		opacity: 0.8;
	}

	70% {
		transform: scale(1.4);
		opacity: 0;
	}

	100% {
		transform: scale(1.4);
		opacity: 0;
	}
}

@media (max-width: 959px) {
	.hero-title {
		font-size: 1.85rem;
	}

	.credit-hero-section {
		padding: 30px 0;
	}

	.parallax-badge {
		display: none;
	}

	.hero-actions {
		justify-content: center;

		.hero-btn-primary,
		.hero-btn-whatsapp {
			width: 100%;
		}
	}

	.floating-whatsapp-trigger {
		bottom: 18px;
		left: 18px;
		width: 54px;
		height: 54px;
	}
}
</style>
